import axios from '../utils/axios';
import { AUTH_STORAGE_KEY, DEFAULT_API_BASE_URL, FILE_TYPE_STATIC } from '../constants';
import type { StaticFile } from '../wysiwyg/types';

type UploadFileIdParams = {
  file: File;
  fileType: number;
  mimeType: string;
  organizationId: number | string;
};

export async function uploadFileId({
  file,
  fileType,
  mimeType,
  organizationId,
}: UploadFileIdParams): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await axios.post('/filesystem', formData, {
    params: { organizationId, fileType, mimeType },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'text',
  });

  return String(response.data || '');
}

export async function getStaticFiles({
  organizationId,
  page = 0,
  pageSize = 50,
}: {
  organizationId: number | string;
  page?: number;
  pageSize?: number;
}): Promise<StaticFile[]> {
  const response = await axios.get('/filesystem/static', {
    params: { organizationId, page, pageSize },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function getFileLink({
  organizationId,
  fileType = FILE_TYPE_STATIC,
  name,
}: {
  organizationId: number | string;
  fileType?: number;
  name: string;
}): Promise<string> {
  const response = await axios.get('/filesystem/link', {
    params: { organizationId, fileType, name },
    responseType: 'text',
  });
  return String(response.data || '');
}

export function getFileContentUrl({
  organizationId,
  fileType = FILE_TYPE_STATIC,
  blobName,
}: {
  organizationId: number | string;
  fileType?: number;
  blobName: string;
}): string {
  const params = new URLSearchParams({
    organizationId: String(organizationId),
    fileType: String(fileType),
    name: blobName,
  });
  return `${DEFAULT_API_BASE_URL}/filesystem?${params.toString()}`;
}

/** Fetch registered image bytes through the authenticated request layer. */
async function requestPreviewImageDataUrl({ organizationId, blobName, legacyFileId }: {
  organizationId: number | string;
  blobName?: string;
  legacyFileId?: string;
}): Promise<string> {
  if (!Number.isFinite(Number(organizationId)) || Number(organizationId) <= 0
    || (!blobName && !legacyFileId)) throw new Error('Invalid preview asset scope.');
  const response = await axios.get(legacyFileId ? '/filesystem/legacy' : '/filesystem', {
    params: legacyFileId ? { organizationId, legacyFileId }
      : { organizationId, fileType: FILE_TYPE_STATIC, name: blobName },
    responseType: 'blob',
  });
  const blob = response.data;
  if (!(blob instanceof Blob) || !blob.size || !/^image\//i.test(blob.type)) {
    throw new Error('Preview image content is unavailable.');
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => typeof reader.result === 'string'
      ? resolve(reader.result) : reject(new Error('Invalid image data.'));
    reader.onerror = () => reject(new Error('Unable to read preview image.'));
    reader.readAsDataURL(blob);
  });
}

const PREVIEW_IMAGE_CACHE_LIMIT = 20;
const PREVIEW_IMAGE_CACHE_TTL = 60_000;
const previewImageSuccesses = new Map<string, { data: string; expiresAt: number }>();
const previewImageRequests = new Map<string, Promise<string>>();
let previewImageSession: string | null | undefined;

export function clearPreviewImageRequestCache(): void {
  previewImageSuccesses.clear();
  previewImageRequests.clear();
  previewImageSession = undefined;
}

/** Share authorized image requests across previews; failures remain retryable. */
export function getPreviewImageDataUrl(options: Parameters<typeof requestPreviewImageDataUrl>[0]): Promise<string> {
  const session = window.localStorage.getItem(AUTH_STORAGE_KEY);
  if (session !== previewImageSession) {
    clearPreviewImageRequestCache();
    previewImageSession = session;
  }
  const key = JSON.stringify([String(options.organizationId), options.legacyFileId ? 'legacy' : 'static',
    options.legacyFileId || options.blobName]);
  const cached = previewImageSuccesses.get(key);
  if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.data);
  previewImageSuccesses.delete(key);
  const existing = previewImageRequests.get(key);
  if (existing) return existing;
  const request = requestPreviewImageDataUrl(options).then(data => {
    // A response from a prior login must not populate the new session's cache.
    if (previewImageRequests.get(key) === request && previewImageSession === session) {
      previewImageSuccesses.set(key, { data, expiresAt: Date.now() + PREVIEW_IMAGE_CACHE_TTL });
      if (previewImageSuccesses.size > PREVIEW_IMAGE_CACHE_LIMIT) {
        previewImageSuccesses.delete(previewImageSuccesses.keys().next().value!);
      }
    }
    return data;
  }).finally(() => {
    if (previewImageRequests.get(key) === request) previewImageRequests.delete(key);
  });
  previewImageRequests.set(key, request);
  return request;
}
